import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Spinner,
    Tooltip
} from "@fluentui/react-components";
import { Attach20Regular, Dismiss24Regular } from "@fluentui/react-icons";
import React, { useRef, useState } from "react";
import { apiClient } from "../api/apiClient";

interface FileUploadButtonProps {
    sessionId: string;
    onUploadSuccess?: (fileInfo: any) => void;
    onUploadError?: (error: string) => void;
    disabled?: boolean;
}

interface UploadedFile {
    filename: string;
    file_hash: string;
    file_size: number;
    message: string;
}

const FileUploadButton: React.FC<FileUploadButtonProps> = ({
    sessionId,
    onUploadSuccess,
    onUploadError,
    disabled = false,
}: FileUploadButtonProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState<{
        type: "success" | "error";
        message: string;
    } | null>(null);

    const ALLOWED_TYPES = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const MAX_SIZE_MB = 10;

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!ALLOWED_TYPES.includes(file.type)) {
            setUploadResult({
                type: "error",
                message: "Invalid file type. Please upload PNG, JPEG, PDF, or DOCX files.",
            });
            onUploadError?.(
                "Invalid file type. Please upload PNG, JPEG, PDF, or DOCX files."
            );
            return;
        }

        // Validate file size
        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
            setUploadResult({
                type: "error",
                message: `File size exceeds ${MAX_SIZE_MB}MB limit.`,
            });
            onUploadError?.(`File size exceeds ${MAX_SIZE_MB}MB limit.`);
            return;
        }

        setSelectedFile(file);
        setUploadResult(null);
        setIsOpen(true);
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        setUploading(true);
        setUploadResult(null);

        try {
            // Always generate a new session ID for file uploads to start fresh analysis
            const newSessionId = `session-${Date.now()}`;

            const formData = new FormData();
            formData.append("file", selectedFile);
            formData.append("session_id", newSessionId);

            const result = await apiClient.upload("/architecture/upload", formData);

            setUploadResult({
                type: "success",
                message: result?.message || "File uploaded successfully!",
            });
            onUploadSuccess?.(result);

            setTimeout(() => {
                handleClose();
            }, result?.session_id ? 1500 : 2000);
        } catch (error: any) {
            const errorMessage = error?.message || "Upload failed. Please try again.";
            setUploadResult({
                type: "error",
                message: errorMessage,
            });
            onUploadError?.(errorMessage);
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
        setSelectedFile(null);
        setUploadResult(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <>
            <input
                ref={fileInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.pdf,.docx"
                onChange={handleFileSelect}
                style={{ display: "none" }}
            />

            <Tooltip
                content={
                    <div style={{ maxWidth: "320px", padding: "4px" }}>
                        <div style={{ fontWeight: "600", marginBottom: "6px", fontSize: "13px" }}>
                            Upload Architecture Files
                        </div>
                        <div style={{ fontSize: "12px", marginBottom: "8px", lineHeight: "1.4" }}>
                            Upload cloud architecture diagrams or infrastructure documents for comprehensive security analysis and Landing Zone recommendation.
                        </div>
                        <div style={{ fontSize: "11px", color: "var(--colorNeutralForeground3)", lineHeight: "1.5" }}>
                            <strong>✓ Supported formats:</strong> PNG, JPEG, PDF, DOCX
                            <br />
                            <strong>✓ Max file size:</strong> 10MB per file
                            <br />
                            <strong>✓ Security:</strong> Automatic virus scanning before processing
                            <br />
                            <strong>✓ Best for:</strong> Multi-cloud architectures (Azure, AWS, GCP), network topology, infrastructure docs
                            <br />
                            <strong>⚠ Limitations:</strong> Files stored temporarily during session only, deleted after analysis. For PDF/DOCX: Only first 9 images from document will be analyzed
                        </div>
                    </div>
                }
                relationship="label"
            >
                <Button
                    appearance="subtle"
                    icon={<Attach20Regular />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled || uploading}
                    aria-label="Upload architecture file"
                />
            </Tooltip>

            <Dialog open={isOpen} onOpenChange={(_event: any, data: any) => !uploading && setIsOpen(data.open)}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle
                            action={
                                <Button
                                    appearance="subtle"
                                    aria-label="close"
                                    icon={<Dismiss24Regular />}
                                    onClick={handleClose}
                                    disabled={uploading}
                                />
                            }
                        >
                            Upload Architecture File
                        </DialogTitle>
                        <DialogContent>
                            {selectedFile && (
                                <div style={{ marginBottom: "16px" }}>
                                    <div
                                        style={{
                                            padding: "12px",
                                            backgroundColor: "var(--colorNeutralBackground2)",
                                            borderRadius: "4px",
                                            marginBottom: "12px",
                                        }}
                                    >
                                        <strong>Selected File:</strong>
                                        <div style={{ marginTop: "8px" }}>
                                            <div>{selectedFile.name}</div>
                                            <div
                                                style={{
                                                    fontSize: "12px",
                                                    color: "var(--colorNeutralForeground3)",
                                                }}
                                            >
                                                {formatFileSize(selectedFile.size)} • {selectedFile.type}
                                            </div>
                                        </div>
                                    </div>

                                    {uploadResult && (
                                        <MessageBar intent={uploadResult.type === "success" ? "success" : "error"}>
                                            <MessageBarBody>
                                                <MessageBarTitle>
                                                    {uploadResult.type === "success" ? "Success" : "Error"}
                                                </MessageBarTitle>
                                                {uploadResult.message}
                                            </MessageBarBody>
                                        </MessageBar>
                                    )}
                                </div>
                            )}

                            <div style={{ fontSize: "14px", color: "var(--colorNeutralForeground3)" }}>
                                <p>Supported formats: PNG, JPEG, PDF, DOCX</p>
                                <p>Maximum size: {MAX_SIZE_MB}MB</p>
                                <p style={{ marginTop: "8px", fontSize: "12px" }}>
                                    Files are automatically scanned for viruses before processing.
                                </p>
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button appearance="secondary" onClick={handleClose} disabled={uploading}>
                                Cancel
                            </Button>
                            <Button
                                appearance="primary"
                                onClick={handleUpload}
                                disabled={!selectedFile || uploading || uploadResult?.type === "success"}
                            >
                                {uploading ? (
                                    <>
                                        <Spinner size="tiny" style={{ marginRight: "8px" }} />
                                        Uploading...
                                    </>
                                ) : (
                                    "Upload"
                                )}
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </>
    );
};

export default FileUploadButton;
